import { BrowserIdentityStore } from "./adapters/browser-identity";
import { DomPageMetaReader } from "./adapters/dom-page-meta";
import { HttpVoteApi } from "./adapters/http-api";
import { readConfig, type SdkConfig } from "./adapters/sdk-config";
import { MyVotesFlow } from "./core/my-votes-flow";
import { PollFlow } from "./core/poll-flow";
import { mountMyPageWidget } from "./ui/my-page-widget";
import { mountPollWidget } from "./ui/poll-widget";
// Tailwindでコンパイル済みのCSSを文字列として読み、各Shadow DOMへ注入する（D-010）。
import widgetStyles from "./widget.css?inline";

// ============================================================
// Vote ウィジェットSDK — composition root
//
// 外側のブラウザアダプタをcoreのポートへ差し込み、UIへ状態機械を渡す。
// 埋め込み先は設置divとdata属性付きscriptの1行だけで利用できる。
// ============================================================

/** Retry-Afterのミリ秒を待つ機能をcoreへ注入し、core内のtimer依存を避ける。 */
function waitMilliseconds(milliseconds: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, milliseconds));
}

/** 投票枠ごとに独立した状態機械を作り、ブラウザアダプタとShadow DOM UIを接続する。 */
function mountPoll(
  container: HTMLElement,
  config: SdkConfig,
  services: {
    api: HttpVoteApi;
    identityStore: BrowserIdentityStore;
    pageMetaReader: DomPageMetaReader;
  },
  onVoteCompleted: () => void,
): void {
  // 二重読込時は既存ウィジェットを維持し、見えない状態機械のAPI呼出しも作らない。
  if (container.shadowRoot !== null) {
    return;
  }
  const flow = new PollFlow({
    siteKey: config.siteKey,
    ...services,
    wait: waitMilliseconds,
  });
  mountPollWidget(container, flow, widgetStyles);
  // 初期表示の回答済み状態ではなく、この画面からの送信成功だけを個人画面の再取得契機にする。
  let previousKind = flow.getState().kind;
  flow.subscribe((state) => {
    if (previousKind === "submitting" && state.kind === "results") {
      onVoteCompleted();
    }
    previousKind = state.kind;
  });
  void flow.start();
}

/** 個人画面枠へ回答履歴専用の状態機械を作り、共有通信・共有IDとUIを接続する。 */
function mountMyPage(
  container: HTMLElement,
  config: SdkConfig,
  services: {
    api: HttpVoteApi;
    identityStore: BrowserIdentityStore;
  },
): MyVotesFlow | null {
  // 二重読込時は既存画面を維持し、同じ履歴のAPI呼出しも重ねない。
  if (container.shadowRoot !== null) {
    return null;
  }
  const flow = new MyVotesFlow({
    siteKey: config.siteKey,
    ...services,
    wait: waitMilliseconds,
  });
  mountMyPageWidget(container, flow, widgetStyles);
  void flow.start();
  return flow;
}

/** ページ内の全設置枠を探索し、種別ごとの画面をマウントする。 */
function init(): void {
  const config = readConfig();
  // 通信とIDを全枠で共有することで、投票と個人画面が並んでもidentifyを1リクエストに束ねる。
  const services = {
    api: new HttpVoteApi({ endpoint: config.endpoint }),
    identityStore: new BrowserIdentityStore(),
    pageMetaReader: new DomPageMetaReader(document, window.location, config.pageTitle),
  };
  const containers = document.querySelectorAll<HTMLElement>("[data-vote-widget]");
  // 同じページで投票が完了したら、配置済みの全個人画面へ最新履歴を取り直させる。
  const myPageFlows: MyVotesFlow[] = [];
  const refreshMyPages = (): void => {
    for (const flow of myPageFlows) {
      void flow.start();
    }
  };

  for (const container of containers) {
    try {
      const kind = container.dataset.voteWidget;
      if (kind === "poll") {
        mountPoll(container, config, services, refreshMyPages);
      } else if (kind === "mypage") {
        const flow = mountMyPage(container, config, services);
        if (flow !== null) {
          myPageFlows.push(flow);
        }
      } else {
        console.warn(`[vote-sdk] 未知のウィジェット種別です: ${kind}`);
      }
    } catch (error) {
      // Shadow DOM非対応要素など1枠の失敗で、後続ウィジェットの初期化を止めない。
      console.warn("[vote-sdk] ウィジェットを初期化できませんでした", error);
    }
  }
}

// defer読み込みと動的読み込みの両方で、設置divが存在してから一度だけ起動する。
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init, { once: true });
} else {
  init();
}
